import BaseAPIs, {iApiBasicResponse, iBasicListingResponse} from "./base.apis";
import axios, {CancelTokenSource} from "axios";
import {iCollectionElement} from "./user.apis";
import {iCountry, iCountryState} from "./common.apis";
import {iSaveAddressFormResponse, RegistrationFeeResponse} from "./author.apis";
import {iProduct} from "./admin/ecomerce/product.apis";


export interface iCart extends iCollectionElement {
    binding_type_id: number
    binding_type_name: string
    checkout_id?: number
    currency: string
    isbn: string
    price_after_discount: number
    price_before_discount: number
    product_id: number
    product_variant_id: number
    publication_date: string
    quantity: number
    session_id?: number
    sub_total: number
    thumbnail_image_url: string
    title: string
    user_id: number,
    discount_percentage: number
    discount_amount: number,
    product?:iProduct
}

export interface iCartResponse extends iApiBasicResponse {
    cart_items: iCart[]
    cart_total_amount: number,
    cart_currency: string,
    cart_total_quantity: number
}

export interface iOrder extends iCollectionElement {
    accept_marketing: boolean

    billing_number?: string
    billing_address_1?: string
    billing_address_2?: string
    billing_address_3?: string
    billing_city?: string
    billing_country_id?: number
    billing_country_state_id?: number
    billing_first_name?: string
    billing_last_name?: string
    billing_middle_name?: string
    billing_same_as_shipping?: boolean
    billing_zip?: string

    cancellation_reason?: string
    cancelled_at?: string
    cancelled_by_user_name?: string
    cart_items: iCart[]
    email_address?: string
    contact_filled_at?: string
    contact_number?: string
    currency?: string
    delivery_category?: string
    delivery_fee?: number
    delivery_fee_filled_at?: string
    delivery_tat_maximum?: number
    delivery_tat_minimum?: number
    grand_total?: number
    order_number?: string
    payment_approved_at?: string
    payment_approved_by_system: true
    payment_last_failure_at?: string
    payment_method?: string
    session_id?: string

    shipping_address_1?: string
    shipping_address_2?: string
    shipping_address_3?: string
    shipping_city?: string

    shipping_number?: string
    shipping_country_id?: number
    shipping_country?: iCountry
    shipping_country_state?: iCountryState
    billing_country_state?: iCountryState
    billing_country?: iCountry
    shipping_country_state_id?: number
    shipping_first_name?: string
    shipping_last_name?: string
    shipping_middle_name?: string
    shipping_zip?: string
    status?: string
    total?: number
    total_quantity?: number
    shipped_at?: string
    user_id?: number
}

export interface iOrderResponse extends iApiBasicResponse {
    order?: iOrder
}

export interface iCheckoutListResponse extends iApiBasicResponse {
    orders?: iBasicListingResponse<iOrder>
}

export const ORDER_STATUS = {
    address_required: "address_required",
    contact_details_required: "contact_details_required",
    delivery_fee_awaited: "delivery_fee_awaited",
    cancelled_by_system: "cancelled_by_system",
    payment_awaited: "payment_awaited",
    payment_failed: "payment_failed",
    payment_initialized: "payment_initialized",
    tracking_details_required: "tracking_details_required",
    cancelled: "cancelled",
    shipped: "shipped",
    confirmed: "confirmed",
}

export function getOrderStatus(status?: string) {
    switch (status) {
        case ORDER_STATUS.delivery_fee_awaited:
            return "Delivery fee awaited"
        case ORDER_STATUS.contact_details_required:
            return "Contact detail required"
        case ORDER_STATUS.address_required:
            return "Address detail required"
        case ORDER_STATUS.payment_awaited:
            return "Payment pending"
        case ORDER_STATUS.payment_initialized:
            return "Payment initialized"
        case ORDER_STATUS.tracking_details_required:
            return "Processing order"
        case ORDER_STATUS.payment_failed:
            return "Payment failed"
        case ORDER_STATUS.cancelled:
            return "Cancelled"
        case ORDER_STATUS.cancelled_by_system:
            return "Auto cancelled"
        case ORDER_STATUS.confirmed:
            return "Shipment pending"
        case ORDER_STATUS.shipped:
            return "Completed"
        default:
            return ""
    }
}

export interface iShippingAddress extends iCollectionElement {
    address_1: string
    address_2: string
    address_3: string
    city: string
    country_id: number
    country_state_id: number
    first_name: string
    last_name: string
    middle_name: string
    number: string
    personal_information_id: number
    zip: string,
    country_state?:iCountryState,
    country:iCountry
}

export interface iBillingAddress extends iCollectionElement {
    address_1: string
    address_2: string
    address_3: string
    city: string
    country_id: number
    country_state_id: number
    first_name: string
    last_name: string
    middle_name: string
    number: string
    personal_information_id: number
    zip: string
    same_as_shipping: boolean
    country_state?:iCountryState,
    country:iCountry
}

export interface EstimateDeliveryResponse extends iApiBasicResponse{
    billing_address?: iBillingAddress,
    deliverable: false,
    delivery_charge:string,
    delivery_category:string,
    delivery_tat_minimum:string,
    delivery_tat_maximum:string,
    grand_total:string,
    accept_marketing:string,
    email_address:string

    shipping_address: iShippingAddress
}

export default class OrderApis extends BaseAPIs {
    add_to_cart = async (product_id: number, quantity: number, product_variant_id: number,
                         add_to_cart?: boolean,allow_foreign?:boolean): Promise<iApiBasicResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        data["product_id"] = product_id;
        data["quantity"] = quantity;
        data["product_variant_id"] = product_variant_id;
        data["add_to_cart"] = add_to_cart || false;
        data["from_dashboard"] = allow_foreign || false;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/cart", data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iApiBasicResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    load_cart = async (): Promise<iCartResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/cart", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iCartResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    checkout = async (id: number[]): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        data["cart_items"] = id
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/checkout", data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    guest_checkout_update = async (id: number, cart_item_id: number, quantity: number): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        data["quantity"] = quantity;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/guest_checkout/" + id + "/update_quantity/" + cart_item_id, data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    checkout_update = async (id: number, cart_item_id: number, quantity: number): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        data["quantity"] = quantity;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/order/" + id + "/update_quantity/" + cart_item_id, data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };

    update_addresses_from_order_data = async (data: any): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/profile/update_addresses_from_order_data", data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    billing_same_as_shipping = async (id: number, billing_same_as_shipping: any): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        data["billing_same_as_shipping"] = billing_same_as_shipping;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/order/" + id + "/billing_same_as_shipping", data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_estimate_delivery = async (): Promise<EstimateDeliveryResponse> => {

        return axios
            .get(this.getApiBaseURL() + "/_api/v1/order/estimate_delivery", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): EstimateDeliveryResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_guest_estimate_delivery = async (): Promise<EstimateDeliveryResponse> => {

        return axios
            .get(this.getApiBaseURL() + "/_api/v1/guest_order/estimate_delivery", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): EstimateDeliveryResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    create_order = async (): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        let data: any = {};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/order", data,{
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    guest_create_order = async (data:any): Promise<iOrderResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + "/_api/v1/guest_order", data,{
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_checkout = async (id: any): Promise<iOrderResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/order/" + id, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_checkout_guest = async (id: any): Promise<iOrderResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/guest_order/" + id, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): iOrderResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_complete_order = async (listingQuery: any, cancel_token?: CancelTokenSource): Promise<iCheckoutListResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/order/completed", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
                params: listingQuery,
                cancelToken: cancel_token && cancel_token.token
            })
            .then((res): iCheckoutListResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_pending_order = async (listingQuery: any, cancel_token?: CancelTokenSource): Promise<iCheckoutListResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/order/pending", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
                params: listingQuery,
                cancelToken: cancel_token && cancel_token.token
            })
            .then((res): iCheckoutListResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    get_cancelled_order = async (listingQuery: any, cancel_token?: CancelTokenSource): Promise<iCheckoutListResponse> => {
        return axios
            .get(this.getApiBaseURL() + "/_api/v1/order/cancelled", {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
                params: listingQuery,
                cancelToken: cancel_token && cancel_token.token
            })
            .then((res): iCheckoutListResponse => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    };
    order_payment_initialize = async (id: number, data: any): Promise<RegistrationFeeResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + `/_api/v1/order/${id}/payment_initialize`, data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    }
    guest_order_payment_initialize = async (id: number, data: any): Promise<RegistrationFeeResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + `/_api/v1/guest_order/${id}/payment_initialize`, data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    }
    guest_order_save_addresses = async (data: any): Promise<iSaveAddressFormResponse> => {
        let csrfToken: string = await new BaseAPIs().getCsrfToken(true);
        if (!csrfToken) return {statusCode: 999, validation_errors: {network: "Network error!"}};
        data["_token"] = csrfToken;
        return axios
            .post(this.getApiBaseURL() + `/_api/v1/guest_order/save_addresses`, data, {
                headers: {"Content-Type": "application/json"},
                withCredentials: true,
            })
            .then((res): any => ({statusCode: res.status, ...res.data}))
            .catch((error): any => {
                return this.handleCatch(error);
            })
    }




}